import React, { useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, MessageCircle, Calendar, Phone, ChevronDown } from 'lucide-react';
import { Button } from './ui/Button';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useTranslation } from '../i18n';
import { orthodonticAppliancesHref } from '../utils/localizedRoutes';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-383" data-mp="31bp57b1ixgf5">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-384" data-mp="36as9gt9ojim2" />
    </svg>;
}
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isServicesHovered, setIsServicesHovered] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const {
    t,
    lang
  } = useTranslation();
  type L = {
    en: string;
    ua: string;
    ru: string;
    ro: string;
  };
  const pick = (o: L) => o[lang as 'en' | 'ua' | 'ru' | 'ro'] || o.en;
  const megaMenuColumns: {
    title: L;
    finance?: boolean;
    items: {
      name: L;
      href: string;
    }[];
  }[] = [{
    title: {
      en: 'General Dentistry',
      ua: 'Загальна стоматологія',
      ru: 'Общая стоматология',
      ro: 'Stomatologie Generală'
    },
    items: [{
      name: {
        en: 'Dental Examination',
        ua: 'Стоматологічний огляд',
        ru: 'Стоматологический осмотр',
        ro: 'Examinare Dentară'
      },
      href: `/${lang}/prices#consultations`
    }, {
      name: {
        en: 'Emergency Dentist',
        ua: 'Екстрена стоматологія',
        ru: 'Неотложная стоматология',
        ro: 'Stomatolog de Urgență'
      },
      href: `/${lang}/emergency-dentist-london`
    }, {
      name: {
        en: 'Dental Hygiene',
        ua: 'Гігієна порожнини рота',
        ru: 'Гигиена полости рта',
        ro: 'Igienă Dentară'
      },
      href: `/${lang}/services/hygiene`
    }, {
      name: {
        en: 'Fillings & Restorations',
        ua: 'Пломби та реставрації',
        ru: 'Пломбы и реставрации',
        ro: 'Plombe și Restaurări'
      },
      href: `/${lang}/services/fillings`
    }, {
      name: {
        en: 'Root Canal Treatment',
        ua: 'Лікування каналів',
        ru: 'Лечение каналов',
        ro: 'Tratament de Canal'
      },
      href: `/${lang}/services/root-canal`
    }, {
      name: {
        en: 'Tooth Extraction',
        ua: 'Видалення зубів',
        ru: 'Удаление зубов',
        ro: 'Extracție Dentară'
      },
      href: `/${lang}/services/extraction`
    }]
  }, {
    title: {
      en: 'Cosmetic Dentistry',
      ua: 'Естетична стоматологія',
      ru: 'Эстетическая стоматология',
      ro: 'Stomatologie Cosmetică'
    },
    finance: true,
    items: [{
      name: {
        en: 'Teeth Whitening',
        ua: 'Відбілювання зубів',
        ru: 'Отбеливание зубов',
        ro: 'Albirea Dinților'
      },
      href: `/${lang}/services/whitening`
    }, {
      name: {
        en: 'Composite Bonding',
        ua: 'Композитний бондинг',
        ru: 'Композитный бондинг',
        ro: 'Bonding Compozit'
      },
      href: `/${lang}/prices#fillings-restorations`
    }, {
      name: {
        en: 'Veneers',
        ua: 'Вініри',
        ru: 'Виниры',
        ro: 'Fațete'
      },
      href: `/${lang}/veneers-london`
    }, {
      name: {
        en: 'Crowns',
        ua: 'Коронки',
        ru: 'Коронки',
        ro: 'Coroane'
      },
      href: `/${lang}/services/crowns`
    }, {
      name: {
        en: 'Smile Makeover',
        ua: 'Перетворення усмішки',
        ru: 'Преображение улыбки',
        ro: 'Transformarea Zâmbetului'
      },
      href: `/${lang}/services/smile-makeover`
    }]
  }, {
    title: {
      en: 'Orthodontics',
      ua: 'Ортодонтія',
      ru: 'Ортодонтия',
      ro: 'Ortodonție'
    },
    finance: true,
    items: [{
      name: {
        en: 'Orthodontics Overview',
        ua: 'Огляд ортодонтії',
        ru: 'Обзор ортодонтии',
        ro: 'Prezentare Ortodonție'
      },
      href: `/${lang}/services/orthodontics`
    }, {
      name: {
        en: 'Orthodontic Appliances',
        ua: 'Ортодонтичні апарати',
        ru: 'Ортодонтические аппараты',
        ro: 'Aparate ortodontice'
      },
      href: orthodonticAppliancesHref(lang)
    }, {
      name: {
        en: 'Clear Aligners',
        ua: 'Прозорі елайнери',
        ru: 'Прозрачные элайнеры',
        ro: 'Aligneri Transparenți'
      },
      href: `/${lang}/services/orthodontics/clear-aligners`
    }, {
      name: {
        en: 'Angel / Invisalign / Spark Clear Aligners',
        ua: 'Елайнери Angel / Invisalign / Spark',
        ru: 'Элайнеры Angel / Invisalign / Spark',
        ro: 'Gutieră ortodontică Angel / Invisalign / Spark'
      },
      href: `/${lang}/services/orthodontics/invisalign-style-aligners`
    }, {
      name: {
        en: 'Metal Braces',
        ua: 'Металеві брекети',
        ru: 'Металлические брекеты',
        ro: 'Aparat Dentar Metalic'
      },
      href: `/${lang}/services/orthodontics/metal-braces`
    }, {
      name: {
        en: 'Ceramic Braces',
        ua: 'Керамічні брекети',
        ru: 'Керамические брекеты',
        ro: 'Aparat Dentar Ceramic'
      },
      href: `/${lang}/services/orthodontics/ceramic-braces`
    }, {
      name: {
        en: 'Retainers',
        ua: 'Ретейнери',
        ru: 'Ретейнеры',
        ro: 'Retaineri'
      },
      href: `/${lang}/services/orthodontics/retainers`
    }, {
      name: {
        en: 'Children’s Orthodontics',
        ua: 'Дитяча ортодонтія',
        ru: 'Детская ортодонтия',
        ro: 'Ortodonție Pediatrică'
      },
      href: `/${lang}/services/orthodontics/children`
    }]
  }, {
    title: {
      en: 'Advanced Treatments',
      ua: 'Складні методи лікування',
      ru: 'Сложные методы лечения',
      ro: 'Tratamente Avansate'
    },
    finance: true,
    items: [{
      name: {
        en: 'Dental Implants',
        ua: 'Імплантація зубів',
        ru: 'Имплантация зубов',
        ro: 'Implanturi Dentare'
      },
      href: `/${lang}/implanty-london`
    }, {
      name: {
        en: 'Oral Surgery',
        ua: 'Хірургічна стоматологія',
        ru: 'Хирургическая стоматология',
        ro: 'Chirurgie Orală'
      },
      href: `/${lang}/prices#extractions`
    }, {
      name: {
        en: 'CBCT / 3D Scan',
        ua: 'КТ / 3D-сканування',
        ru: 'КТ / 3D-сканирование',
        ro: 'CBCT / Scanare 3D'
      },
      href: `/${lang}/prices#radiography`
    }, {
      name: {
        en: 'Digital Dentistry',
        ua: 'Цифрова стоматологія',
        ru: 'Цифровая стоматология',
        ro: 'Stomatologie Digitală'
      },
      href: `/${lang}/about`
    }, {
      name: {
        en: 'Microscopic Dentistry',
        ua: 'Мікроскопічна стоматологія',
        ru: 'Микроскопическая стоматология',
        ro: 'Stomatologie Microscopică'
      },
      href: `/${lang}/about`
    }]
  }];
  const menuStrings = {
    promoTitle: {
      en: 'Angel / Invisalign / Spark Clear Aligners from £3,750',
      ua: 'Елайнери Angel / Invisalign / Spark від £3,750',
      ru: 'Элайнеры Angel / Invisalign / Spark от £3,750',
      ro: 'Gutiere ortodontice Angel / Invisalign / Spark de la £3,750'
    } as L,
    promoSubtitle: {
      en: 'Discreet teeth straightening in Angel, London.',
      ua: 'Непомітне вирівнювання зубів в Angel, Лондон.',
      ru: 'Незаметное выравнивание зубов в Angel, Лондон.',
      ro: 'Îndreptarea discretă a dinților în Angel, Londra.'
    } as L,
    bookConsultation: {
      en: 'Book Consultation',
      ua: 'Записатися на консультацію',
      ru: 'Записаться на консультацию',
      ro: 'Programare Consultație'
    } as L,
    finance: {
      en: '0% finance available for up to 12 months on selected treatments.',
      ua: '0% фінансування до 12 місяців на окремі види лікування.',
      ru: '0% рассрочка до 12 месяцев на отдельные виды лечения.',
      ro: 'Finanțare 0% disponibilă până la 12 luni pentru anumite tratamente.'
    } as L,
    askFinance: {
      en: 'Ask About Finance',
      ua: 'Дізнатися про розстрочку',
      ru: 'Узнать о рассрочке',
      ro: 'Întreabă despre Finanțare'
    } as L
  };
  const navLabels = {
    patientExperience: {
      en: 'Patient Experience',
      ua: 'Досвід пацієнта',
      ru: 'Опыт пациента',
      ro: 'Experiența Pacientului'
    } as L,
    results: {
      en: 'Results',
      ua: 'Результати',
      ru: 'Результаты',
      ro: 'Rezultate'
    } as L,
    blog: {
      en: 'Blog',
      ua: 'Блог',
      ru: 'Блог',
      ro: 'Blog'
    } as L
  };
  const navigation = [{
    name: t('nav.about'),
    href: `/${lang}/about`
  }, {
    name: t('nav.services'),
    href: `/${lang}/services`
  }, {
    name: t('nav.prices'),
    href: `/${lang}/prices`
  }, {
    name: pick(navLabels.patientExperience),
    href: `/${lang}/patient-experience`
  }, {
    name: pick(navLabels.results),
    href: `/${lang}/results`
  }, {
    name: pick(navLabels.blog),
    href: `/${lang}/blog`
  }, {
    name: t('nav.contact'),
    href: `/${lang}/contact`
  }];
  return <header className="sticky top-0 z-50 w-full bg-ivory/85 backdrop-blur-md border-b border-stone-200" onMouseLeave={() => setIsServicesHovered(false)} data-id="element-385" data-mp="2n0466u62oz6u">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-386" data-mp="2da2kua53tuvm">
        <div className="flex justify-between items-center h-20" data-id="element-387" data-mp="1zbl08lahwyhx">
          {/* Logo — minimal architectural wordmark */}
          <Link to={`/${lang}`} className="flex flex-col leading-none" data-id="element-388" data-mp="23d6iyn9po701">
            <span className="text-2xl font-semibold tracking-[0.22em] text-forest-700" style={{
            fontFamily: '"Playfair Display", Georgia, serif'
          }} data-id="element-389" data-mp="1pd5aomr49xaj">
              DENTICO
            </span>
            <span className="block text-right text-[10px] tracking-[0.4em] text-forest-700 uppercase mt-0.5" data-id="element-390" data-mp="3jrzeq45z91qn">
              Clinic
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6 h-full" data-id="element-391" data-mp="3naf2jaoijxpz">
            {navigation.map(item => {
            if (item.name === t('nav.services')) {
              return <div key={item.href} className="h-full flex items-center" onMouseEnter={() => setIsServicesHovered(true)} data-id="element-392" data-mp="3db6boqoy2z7u">
                    <Link to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex items-center gap-1" data-id="element-393" data-mp="9tftay0iuyze">
                      {item.name}
                      <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isServicesHovered ? 'rotate-180 text-forest-600' : ''}`} data-id="element-394" data-mp="3rbh3px1zqjul" />
                    </Link>
                  </div>;
            }
            if (item.href.endsWith('/patient-experience')) {
              const words = item.name.split(' ');
              return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex flex-col items-center text-center leading-[1.1]" data-id="element-395" data-mp="104zmh2rzq4wp">
                    {words.map((w, i) => <span key={i} data-id="element-396" data-mp="1f18jdxoes65y" data-mpi={i}>{w}</span>)}
                  </Link>;
            }
            if (item.href.includes('/patient-experience')) {
              return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors flex flex-col items-center justify-center text-center leading-[1.15]" data-id="element-397" data-mp="1nvviyd7e5oaa">
                    {item.name.split(' ').map((word, i) => <span key={i} data-id="element-398" data-mp="2zherpggvigrj" data-mpi={i}>{word}</span>)}
                  </Link>;
            }
            return <Link key={item.href} to={item.href} className="text-sm font-medium text-stone-600 hover:text-forest-600 transition-colors" data-id="element-399" data-mp="2uhivw4ibxvn2">
                  {item.name}
                </Link>;
          })}
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4" data-id="element-400" data-mp="2blalkmagmukw">
            <LanguageSwitcher data-id="element-401" data-mp="2pkack0lg3lk8" />
            <div className="h-6 w-px bg-beige/60" data-id="element-402" data-mp="2pkip1rdnoouw" />
            <a href="tel:+447345197820" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="Phone" data-id="element-403" data-mp="2pi62f0wydqbi">
              <Phone className="w-5 h-5" data-id="element-404" data-mp="4s89isoe4t61" />
            </a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="WhatsApp" data-id="element-405" data-mp="t7ttj17m6n9">
              <MessageCircle className="w-5 h-5" data-id="element-406" data-mp="d8z5m5hkprlh" />
            </a>
            <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-forest-600 transition-colors" aria-label="Telegram" data-id="element-407" data-mp="3ukgzhc3aflsa">
              <TelegramIcon className="w-5 h-5" data-id="element-408" data-mp="w1jt17ei74hw" />
            </a>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-409" data-mp="w107lgpn9xz4">
              <Button size="sm" className="gap-2" data-id="element-410" data-mp="hblxg6e8vcio">
                <Calendar className="w-4 h-4" data-id="element-411" data-mp="mbqim9q3kvf8" />
                {t('nav.bookOnline')}
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2 flex-shrink-0" data-id="element-412" data-mp="19zzwmph2kevt">
            <LanguageSwitcher data-id="element-413" data-mp="1est3pk00fzb2" />
            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="relative z-50 flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 text-charcoal hover:text-forest-700 active:bg-stone-100 rounded-lg transition-colors" aria-label={isMenuOpen ? 'Close menu' : 'Open menu'} type="button" data-id="element-414" data-mp="1eu44day5sqxy">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-415" data-mp="1aql9pk6ovo0a" /> : <Menu className="w-6 h-6" data-id="element-416" data-mp="15qqfp7rmaib0" />}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Mega Menu Dropdown — compact, attached, subtle */}
      <div className={`hidden md:block absolute top-full left-0 w-full bg-ivory border-t border-stone-200 shadow-[0_14px_28px_-18px_rgba(47,74,63,0.22)] transition-all duration-200 origin-top ${isServicesHovered ? 'opacity-100 scale-y-100 visible' : 'opacity-0 scale-y-95 invisible'}`} onMouseEnter={() => setIsServicesHovered(true)} onMouseLeave={() => setIsServicesHovered(false)} data-id="element-417" data-mp="w05klub5jvwu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-418" data-mp="2tmeq6a5mg1jn">
          <div className="flex gap-7" data-id="element-419" data-mp="2yl84g4sqfwkv">
            {/* Columns */}
            <div className="flex-grow grid grid-cols-4 gap-5 divide-x divide-stone-200/70" data-id="element-420" data-mp="33l48u55cdq0y">
              {megaMenuColumns.map((col, idx) => <div key={idx} className={`flex flex-col ${idx > 0 ? 'pl-5' : ''}`} data-id="element-421" data-mp="37leipsw990l3" data-mpi={idx}>
                  <h3 className="text-forest-700 font-semibold mb-3 text-[11px] uppercase tracking-[0.15em]" style={{
                fontFamily: '"DM Sans", sans-serif'
              }} data-id="element-422" data-mp="2aulzwnhxsufm">
                    {pick(col.title)}
                  </h3>
                  <ul className="space-y-1" data-id="element-423" data-mp="1s6765sg8j2b4">
                    {col.items.map((link, linkIdx) => <li key={linkIdx} data-id="element-424" data-mp="1w70w0rd8mr6n" data-mpi={linkIdx}>
                        <Link to={link.href} className="text-sm text-stone-600 hover:text-forest-700 hover:bg-stone-100/70 -mx-2 px-2 py-1 rounded-md transition-colors block" onClick={() => setIsServicesHovered(false)} data-id="element-425" data-mp="1w5zm4q7ykaqi">
                          {pick(link.name)}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>

            {/* Promo Card — compact & elegant */}
            <div className="w-[230px] flex-shrink-0" data-id="element-426" data-mp="13iy7tmp82gjc">
              <div className="bg-forest-900 border border-white/10 rounded-xl p-4 h-full flex flex-col justify-between" data-id="element-427" data-mp="13i57b3guwn9n">
                <div data-id="element-428" data-mp="1da2iwbbs1h8w">
                  <h4 className="text-base font-semibold text-ivory mb-1.5 leading-snug" data-id="element-429" data-mp="1dau6ayv808o3">
                    {pick(menuStrings.promoTitle)}
                  </h4>
                  <p className="text-xs text-ivory/70 mb-3 leading-relaxed" data-id="element-430" data-mp="1n7ysgyp2g44w">
                    {pick(menuStrings.promoSubtitle)}
                  </p>
                </div>
                <div className="space-y-2" data-id="element-431" data-mp="25u6d9xf1wuen">
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-432" data-mp="2avvnjaug941o">
                    <Button size="sm" fullWidth className="bg-[#F8F5F0] text-[#2F4A3F] border border-[#F8F5F0] hover:bg-[#EFE8DE] hover:text-[#2F4A3F] shadow-sm font-medium" data-id="element-433" data-mp="1wxxd2gj91rrg">
                      {pick(menuStrings.bookConsultation)}
                    </Button>
                  </a>
                  <Link to={`/${lang}/contact`} onClick={() => setIsServicesHovered(false)} data-id="element-434" data-mp="1e6o5wdwybf31">
                    <Button size="sm" fullWidth className="bg-transparent border border-[#F8F5F0] text-[#F8F5F0] hover:bg-[#F8F5F0] hover:text-[#2F4A3F]" data-id="element-435" data-mp="1476wblekwh1s">
                      {pick(menuStrings.askFinance)}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          {/* Full-width finance line */}
          <div className="border-t border-[#D8C4A8]/70 bg-[#EFE8DE]" data-id="element-436" data-mp="yejh2blwjhu6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-center gap-4" data-id="element-437" data-mp="13cb8l4imx1xu">
              <p className="text-[13px] leading-snug text-[#2F4A3F] text-center" data-id="element-438" data-mp="1r1w38ko5hnmy">
                <span className="font-semibold" data-id="element-439" data-mp="1vyvzk3v77c0v">0%</span>{' '}
                {pick(menuStrings.finance).replace(/^0% ?/i, '')}
              </p>
              <Link to={`/${lang}/contact`} onClick={() => setIsServicesHovered(false)} className="hidden lg:inline-flex items-center text-[13px] font-medium text-[#2F4A3F] underline decoration-[#D8C4A8] underline-offset-4 hover:decoration-[#2F4A3F] transition-colors whitespace-nowrap" data-id="element-440" data-mp="1mae1vvi7tl1g">
                {pick(menuStrings.askFinance)}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && <div className="md:hidden border-t border-beige/60 bg-ivory relative z-50 max-h-[calc(100vh-80px)] overflow-y-auto" data-id="element-441" data-mp="c4jhfergcs44">
          <div className="px-4 py-6 space-y-4" data-id="element-442" data-mp="83pq0beswv6h">
            <nav className="flex flex-col space-y-4" data-id="element-443" data-mp="3gi86n1it5j1p">
              {navigation.map(item => {
            if (item.name === t('nav.services')) {
              return <div key={item.href} className="flex flex-col space-y-3" data-id="element-444" data-mp="3blhom8ng0ddn">
                      <div className="flex items-center justify-between" data-id="element-445" data-mp="3pk7lpoa9id20">
                        <Link to={item.href} className="text-lg font-medium text-charcoal" onClick={() => setIsMenuOpen(false)} data-id="element-446" data-mp="3kis3s53dsmnu">
                          {item.name}
                        </Link>
                        <button onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)} className="p-2 -mr-2 text-charcoal-light" data-id="element-447" data-mp="3tksju9glwm0p">
                          <ChevronDown className={`w-5 h-5 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} data-id="element-448" data-mp="bgh8du0ofxnx" />
                        </button>
                      </div>
                      {isMobileServicesOpen && <div className="pl-4 space-y-6 pb-2 border-l-2 border-stone-200 ml-1" data-id="element-449" data-mp="3jw0v0a6uehu0">
                          {megaMenuColumns.map((col, idx) => <div key={idx} data-id="element-450" data-mp="3onsek0rkznan" data-mpi={idx}>
                              <h3 className="text-forest-700 font-semibold mb-3 text-xs uppercase tracking-[0.15em]" style={{
                      fontFamily: '"DM Sans", sans-serif'
                    }} data-id="element-451" data-mp="2r7mxdvjo1niz">
                                {pick(col.title)}
                              </h3>
                              <ul className="space-y-3" data-id="element-452" data-mp="2enpyjl2kxg1t">
                                {col.items.map((link, linkIdx) => <li key={linkIdx} data-id="element-453" data-mp="2amcpl246wwqo" data-mpi={linkIdx}>
                                    <Link to={link.href} className="text-base text-stone-600 block" onClick={() => setIsMenuOpen(false)} data-id="element-454" data-mp="25ob98oy5fbiw">
                                      {pick(link.name)}
                                    </Link>
                                  </li>)}
                              </ul>
                            </div>)}
                          <div className="mt-6 bg-forest-900 border border-white/10 rounded-xl p-5" data-id="element-455" data-mp="1hz0ebo3hcbco">
                            <h4 className="font-semibold text-ivory mb-1" data-id="element-456" data-mp="3i2kv7kf5q1qy">
                              {pick(menuStrings.promoTitle)}
                            </h4>
                            <p className="text-sm text-ivory/70 mb-4" data-id="element-457" data-mp="389vlov362xy9">
                              {pick(menuStrings.promoSubtitle)}
                            </p>
                            <div className="space-y-2" data-id="element-458" data-mp="2ycqzwwd8h4hz">
                              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-459" data-mp="2yb62myzjb7gf">
                                <Button size="sm" fullWidth className="bg-[#F8F5F0] text-[#2F4A3F] border border-[#F8F5F0] hover:bg-[#EFE8DE] hover:text-[#2F4A3F] font-medium" data-id="element-460" data-mp="2uavx1nfnmj23">
                                  {pick(menuStrings.bookConsultation)}
                                </Button>
                              </a>
                              <Link to={`/${lang}/contact`} onClick={() => setIsMenuOpen(false)} data-id="element-461" data-mp="xpo1nr02xzwd">
                                <Button size="sm" fullWidth className="bg-transparent border border-[#F8F5F0] text-[#F8F5F0] hover:bg-[#F8F5F0] hover:text-[#2F4A3F]" data-id="element-462" data-mp="16o9dl3b1qgo6">
                                  {pick(menuStrings.askFinance)}
                                </Button>
                              </Link>
                            </div>
                          </div>
                          {/* Single finance block */}
                          <div className="mt-6 rounded-xl border border-[#D8C4A8]/70 bg-[#EFE8DE] px-4 py-3.5 space-y-2" data-id="element-463" data-mp="3s4oil6p95q3g">
                            <p className="text-sm leading-snug text-[#2F4A3F]" data-id="element-464" data-mp="1v4u35r6d0nxj">
                              <span className="font-semibold" data-id="element-465" data-mp="202c0slexfgq8">0%</span>{' '}
                              {pick(menuStrings.finance).replace(/^0% ?/i, '')}
                            </p>
                            <Link to={`/${lang}/contact`} onClick={() => setIsMenuOpen(false)} className="inline-flex items-center text-sm font-medium text-[#2F4A3F] underline decoration-[#D8C4A8] underline-offset-4" data-id="element-466" data-mp="2a0rumw5c943f">
                              {pick(menuStrings.askFinance)}
                            </Link>
                          </div>
                        </div>}
                    </div>;
            }
            return <Link key={item.href} to={item.href} className="text-lg font-medium text-charcoal" onClick={() => setIsMenuOpen(false)} data-id="element-467" data-mp="31rxxhkcg1fwe">
                    {item.name}
                  </Link>;
          })}
            </nav>
            <div className="pt-4 border-t border-beige/60 space-y-3" data-id="element-468" data-mp="h4z3a2miko41">
              <a href="tel:+447345197820" className="block" data-id="element-469" data-mp="c5w5924ybi5k">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-470" data-mp="c7fo90hzs7b1">
                  <Phone className="w-4 h-4" data-id="element-471" data-mp="19sbp2akuorwa" />
                  07345 197820
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-472" data-mp="14vuxt7ictxez">
                <Button variant="whatsapp" fullWidth className="gap-2" data-id="element-473" data-mp="v3ffg935b6tk">
                  <MessageCircle className="w-4 h-4" data-id="element-474" data-mp="1ske81skysk9p" />
                  WhatsApp
                </Button>
              </a>
              <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-475" data-mp="1nr1lrp3rcpk4">
                <Button variant="outline" fullWidth className="gap-2" data-id="element-476" data-mp="10wtame9tzjh0">
                  <TelegramIcon className="w-4 h-4" data-id="element-477" data-mp="n4mwexq8nmk7" />
                  Telegram
                </Button>
              </a>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block" data-id="element-478" data-mp="r56p9p3s440j">
                <Button fullWidth className="gap-2" data-id="element-479" data-mp="1judoyreyyznd">
                  <Calendar className="w-4 h-4" data-id="element-480" data-mp="1jtuhy5quxvo4" />
                  {t('nav.bookOnline')}
                </Button>
              </a>
            </div>
          </div>
        </div>}
    </header>;
}